import { Fragment } from 'react';
import { Stack, Table, Text } from '@mantine/core';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { COLORS, LIGHT_GREY_BG } from 'constants/colors';
import { ANALYSIS, PRIMARY_ANALYSIS, SECONDARY_ANALYSIS } from 'constants/pricing';

import useBroswerLanguage from 'util/hooks/useLanguage';
import { defaultPricingQueryOptions } from 'util/queryOptions';
import { sortByValue } from 'util/sortByName';
import { convertUnderscoresToSpaces } from 'util/stringUtils';
import { getString } from 'strings/translation';
import { DefaultPriceType, SkuType } from 'store/pricing/types';
import CenteredLoader from 'common/CenteredLoader';

import DefaultSkuPriceRow from './DefaultSkuPriceRow';
import DefaultSkuPrices from './DefaultSkuPrices';

type Props = {
  skuQuery: UseQueryResult<SkuType[], Error>;
};

type AnalysisPriceType = Record<
  | 'primary_analysis'
  | 'secondary_analysis'
  | 'sampling'
  | 'scan'
  | 'adcp'
  | 'pro_map_generation'
  | 'till_rx_map_generation',
  DefaultPriceType[]
>;

const INITIAL_PRICE_GROUPS: AnalysisPriceType = {
  primary_analysis: [],
  secondary_analysis: [],
  sampling: [],
  scan: [],
  adcp: [],
  pro_map_generation: [],
  till_rx_map_generation: [],
};

const PricingTable = ({ skuQuery }: Props) => {
  const language = useBroswerLanguage();
  const defaultPricingQuery = useQuery(defaultPricingQueryOptions);

  if (defaultPricingQuery.isPending || skuQuery.isPending) {
    return <CenteredLoader />;
  }

  const analysisPrices = Object.values(defaultPricingQuery.data || {}).reduce((acc, price) => {
    const { type, is_primary } = price.sku;
    if (type === ANALYSIS) {
      const key = is_primary ? PRIMARY_ANALYSIS : SECONDARY_ANALYSIS;
      acc[key].push(price);
    } else {
      acc[type]?.push(price);
    }

    return acc;
  }, structuredClone(INITIAL_PRICE_GROUPS));

  const getDefaultRow = (header: string, bg?: string, textColor?: string): JSX.Element => (
    <Table.Tr bg={bg}>
      <Table.Td>
        <Text size="sm" c={textColor}>
          {header}
        </Text>
      </Table.Td>
      {Array.from({ length: 8 }).map((_, i) => (
        <Table.Td key={i} />
      ))}
    </Table.Tr>
  );

  return (
    <Stack>
      <Table withTableBorder withColumnBorders stickyHeader>
        <Table.Thead>
          <Table.Tr>
            {[
              'productName',
              'priceType',
              'price',
              'listPrice',
              'minDensity',
              'maxDensity',
              'minScanDensity',
              'maxScanDensity',
              'notes',
            ].map((key) => (
              <Table.Th key={key}>{getString(key, language)}</Table.Th>
            ))}
          </Table.Tr>
          {getDefaultRow('*NEW*', LIGHT_GREY_BG)}
          <DefaultSkuPriceRow skuOptions={skuQuery.data} />
        </Table.Thead>

        <Table.Tbody>
          {Object.entries(analysisPrices).map(([key, prices]) => (
            <Fragment key={key}>
              {getDefaultRow(
                convertUnderscoresToSpaces(key).toUpperCase(),
                COLORS.greyBlue,
                COLORS.white,
              )}
              {sortByValue(prices, 'sku_name').map((skuPrice) => (
                <DefaultSkuPrices
                  key={skuPrice.sku.id}
                  defaultPriceType={skuPrice}
                  skuOptions={skuQuery.data}
                />
              ))}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default PricingTable;
